// @flow
import classnames from 'classnames';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import Typography from '@material-ui/core/Typography';

import Phone from '../../../assets/icons/phone.png';

import ConversationButton from './ConversationButton';
import JoinConversationButton from './JoinConversationButton';

import RoundedButton from '../../components/Buttons/RoundedButton';
import MicIcon from '../../components/Icons/Mic';
import PhraseMarkdownText from '../../components/PhraseMarkdownText';

import ConferenceCallPopupContainer from '../scribe/ConferenceCallPopupContainer';

import { loggingOut, clearScribeConversationState, firebaseLogout } from '../../actions';

import { logoutAndClean } from '../../utils/user';

const useStyles = makeStyles(theme => ({
  workLogo: {
    height: '100%',
    marginTop: -28,
    marginBottom: 2,
    [theme.breakpoints.up('xl')]: {
      height: 'unset !important',
      width: '100%',
    },
    [theme.breakpoints.down('xl')]: {
      height: 400,
    },
    [theme.breakpoints.down('md')]: {
      height: 350,
    },
  },
  title: {
    fontSize: 40,
    fontWeight: 400,
    marginBottom: 20,
    [theme.breakpoints.down('md')]: {
      fontSize: 34,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 28,
    },
  },
  conversationOptions: {
    width: '100%',
    maxWidth: 537,
    '& > *': {
      marginBottom: 20,
    },
  },
  icon: {
    position: 'absolute',
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  startCaptionsIcon: {
    top: 5,
    left: 20,
  },
  text: {
    fontSize: 20,
    lineHeight: '26px',
  },
  conferenceCallButton: {
    backgroundColor: theme.palette.ava.scribe2,
    color: theme.palette.ava.white,
    '&:hover': {
      backgroundColor: theme.palette.ava.scribe1,
    },
  },
  phoneIcon: {
    top: 18,
    left: 28,
  },
  signInLink: {
    alignItems: 'center',
    background: theme.palette.ava.blue,
    borderRadius: 100,
    color: 'white',
    display: 'flex',
    justifyContent: 'center',
    margin: '0 auto 16px',
    textAlign: 'center',
    width: '100%',
    whiteSpace: 'nowrap',
    '&:hover': {
      textDecoration: 'none',
      color: 'white',
    },
  },
  signInButton: {
    width: '100%',
    height: 56,
    fontSize: 20,
  },
  link: {
    fontWeight: 600,
  },
}));

interface Props {
  clearScribeConversationState: Function;
  host?: boolean;
  twilio?: boolean;
  onHost?: Function;
  firebase: Object;
  firebaseLogout: Function;
  lightView: boolean;
  loggingOut: boolean;
  logOut: Function;
}

const Landing = ({ host, twilio, onHost, firebase, firebaseLogout, lightView, loggingOut, logOut }: Props) => {
  const classes = useStyles();

  const [twilioPopup, setTwilioPopup] = useState(false);

  if (lightView) return null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {twilioPopup && <ConferenceCallPopupContainer on={twilioPopup} setOff={() => setTwilioPopup(false)} />}
      <img src="/work.svg" alt="ava work logo" className={classes.workLogo} />
      <Typography variant="h4" align="center" className={classes.title}>
        <PhraseMarkdownText phraseKey="joinAConversation" />
      </Typography>
      <div className={classes.conversationOptions}>
        {(!firebase?.user || firebase?.user?.isAnonymous) && !loggingOut && (
          <RoundedButton
            className={classes.signInButton}
            color="blue"
            disableRipple
            onClick={async () => {
              logOut();
              await logoutAndClean();
              clearScribeConversationState();
              firebaseLogout();
              window.history.pushState({}, document.title, '/web');
              window.location.reload();
            }}
          >
            <PhraseMarkdownText phraseKey="signUpForCaptions" />
          </RoundedButton>
        )}
        {host && (
          <ConversationButton color="blue" onClick={onHost}>
            <div className={classnames(classes.icon, classes.startCaptionsIcon)}>
              <MicIcon width={36} height={44} />
            </div>
            <PhraseMarkdownText className={classes.text} phraseKey="sidebar.buttons.startCaptions" />
          </ConversationButton>
        )}
        {twilio && (
          <ConversationButton className={classes.conferenceCallButton} onClick={() => setTwilioPopup(true)}>
            <img className={classnames(classes.icon, classes.phoneIcon)} src={Phone} alt="phone" height="20" />
            <PhraseMarkdownText className={classes.text} phraseKey="conversation.call.joinShort" />
          </ConversationButton>
        )}
        <JoinConversationButton />
      </div>
    </div>
  );
};

const mapStateToProps = ({ firebase, scribeSession: { lightView, loggingOut } }) => ({
  firebase,
  lightView,
  loggingOut,
});

const mapDispatchToProps = {
  clearScribeConversationState,
  firebaseLogout,
  logOut: loggingOut,
};

export default connect(mapStateToProps, mapDispatchToProps)(Landing);
